import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { FileText, Plus, CaretDown } from "@phosphor-icons/react";
import { Pill } from "../hsUtils";
import { RAMS_TONE, RAMS_LABEL } from "./ramsUtils";

const KEY = { job: "job_id", emergency_job: "emergency_job_id", job_request: "request_id" };
const LIST_PARAM = { job: "job_id", emergency_job: "job_id", job_request: "request_id" };

/** Header button on job requests, jobs and emergency jobs: drafts a RAMS pre-filled from the record (client, site, scope,
 *  operatives, nearest A&E) and opens the editor. Existing RAMS on the record are listed in the popover. */
export function CreateRamsButton({ entityType = "job", entityId, variant = "outline", size = "sm", className = "", onCreated }) {
  const navigate = useNavigate();
  const [rams, setRams] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!entityId) return;
    API.get("/hs/rams", { params: { [LIST_PARAM[entityType]]: entityId } }).then((r) => setRams((r.data.rams || []).filter((x) => x.status !== "archived"))).catch(() => setRams([]));
  }, [entityType, entityId]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/hs/rams", { [KEY[entityType]]: entityId });
      toast.success(`${data.number} drafted${data.sections?.emergency?.nearest_ae ? ` — nearest A&E: ${data.sections.emergency.nearest_ae.name}` : ""}`);
      onCreated && onCreated(data);
      navigate(`/health-safety/rams/${data.id}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't create the RAMS"); } finally { setBusy(false); }
  };
  if (!rams?.length) {
    return <Button variant={variant} size={size} className={className} disabled={busy} onClick={create} data-testid="create-rams-btn"><FileText size={14} className="mr-1" /> {busy ? "Drafting…" : "Create RAMS"}</Button>;
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={variant} size={size} className={className} data-testid="create-rams-btn"><FileText size={14} className="mr-1" /> RAMS <span className="ml-1 rounded-full bg-primary/15 text-primary px-1.5 text-[10px] font-semibold" data-testid="create-rams-count">{rams.length}</span><CaretDown size={12} className="ml-1" /></Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2" data-testid="create-rams-popover">
        <p className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">RAMS on this {entityType === "job_request" ? "request" : entityType === "emergency_job" ? "emergency job" : "job"}</p>
        {rams.map((r) => (
          <Link key={r.id} to={`/health-safety/rams/${r.id}`} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted" data-testid={`create-rams-existing-${r.id}`}>
            <span className="font-mono text-muted-foreground">{r.number}</span><span className="flex-1 truncate">{r.title}</span><Pill tone={RAMS_TONE[r.status]}>{RAMS_LABEL[r.status]}</Pill>
          </Link>
        ))}
        <Button size="sm" className="w-full mt-2 h-8" disabled={busy} onClick={create} data-testid="create-rams-new"><Plus size={14} className="mr-1" /> {busy ? "Drafting…" : "Create another RAMS"}</Button>
      </PopoverContent>
    </Popover>
  );
}
